'use client';

import { CloseIcon } from '@/components/icons/close-icon';
import { cn } from '@/lib/utils';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as React from 'react';

const Dialog = ({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>): React.JSX.Element => {
  return <DialogPrimitive.Root data-slot="dialog"
    {...props}
  />;
};

const DialogTrigger = ({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger> & {
  asChild?: boolean;
}): React.JSX.Element => {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger"
    {...props}
  />;
};

const DialogPortal = ({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>): React.JSX.Element => {
  return <DialogPrimitive.Portal data-slot="dialog-portal"
    {...props}
  />;
};

const DialogClose = ({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>): React.JSX.Element => {
  return <DialogPrimitive.Close data-slot="dialog-close"
    {...props}
  />;
};

const DialogOverlay = ({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>): React.JSX.Element => {
  return (
    <DialogPrimitive.Overlay className={cn(
      'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-200 bg-black/50',
      className
    )}
    data-slot="dialog-overlay"
    {...props}
    />
  );
};

const DialogContent = ({
  children,
  className,
  onCloseAutoFocus,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  onCloseAutoFocus?: (e: Event) => void;
}): React.JSX.Element => {
  const handleCloseAutoFocus = (e: Event): void => {
    onCloseAutoFocus?.(e);
    e.preventDefault();
  };
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      <DialogPrimitive.Content className={cn(
        'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
        'data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%]',
        'left-[50%] z-200 grid w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 gap-4 rounded-sm',
        'border p-ms shadow-lg duration-200',
        className
      )}
      data-slot="dialog-content"
      onCloseAutoFocus={handleCloseAutoFocus}
      {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
};

type DialogHeaderProps = {
  showCloseButton?: boolean;
} & React.ComponentProps<'div'>;

const DialogHeader = ({
  className,
  showCloseButton = true,
  ...props
}: DialogHeaderProps): React.JSX.Element => {
  return (
    <div className="flex items-start justify-between">
      <div className={cn('flex flex-col gap-2 text-center sm:text-left', className)}
        data-slot="dialog-header"
        {...props}
      />

      {showCloseButton &&
        <DialogPrimitive.Close className={cn(
          'ring-offset-background focus-visible:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground',
          'rounded-xs opacity-70 transition-opacity hover:opacity-100',
          'focus-visible:ring-2 focus-visible:ring-offset-2 focus:outline-hidden disabled:pointer-events-none',
          '[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*="size-"])]:size-6'
        )}
        data-test="dialog-close-x"
        >
          <CloseIcon className="text-primary" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      }
    </div>
  );
};

const DialogFooter = ({ className, ...props }: React.ComponentProps<'div'>): React.JSX.Element => {
  return (
    <div className={cn(
      'flex flex-col-reverse gap-2 sm:flex-row sm:justify-end',
      className
    )}
    data-slot="dialog-footer"
    {...props}
    />
  );
};

const DialogTitle = ({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>): React.JSX.Element => {
  return (
    <DialogPrimitive.Title className={cn('text-lg leading-none font-semibold', className)}
      data-slot="dialog-title"
      {...props}
    />
  );
};

const DialogDescription = ({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>): React.JSX.Element => {
  return (
    <DialogPrimitive.Description className={cn('text-muted-foreground text-sm', className)}
      data-slot="dialog-description"
      {...props}
    />
  );
};

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
